import * as React from 'react';
import { classNames, useUid } from '@/utils';

export interface TextFieldProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'prefix'> {
  /** The ID of the input element. */
  id?: string;
  /** An error message to display below the input, if applicable. */
  error?: string;
  /** Whether the input should be disabled. */
  disabled?: boolean;
  /** Placeholder text to display in the input. */
  placeholder?: string;
  /** The default value of the input. */
  defaultValue?: string;
  /** CSS class names to apply to the label. */
  labelClassName?: string;
  /** CSS class names to apply to the input. */
  inputClassName?: string;
  /** The label to display above the input. */
  label?: React.ReactNode;
  /** A prefix to display before the input. */
  prefix?: React.ReactNode;
  /** A suffix to display after the input. */
  suffix?: React.ReactNode;
  /** Help text to display below the input. */
  helpText?: React.ReactNode;
  /** A section to display on the right side of the input. */
  rightSection?: React.ReactNode;
  /** A callback function that is called when the value of the input changes. */
  onChange?(value: string): void;
  /** CSS styles to apply to the input. */
  inputStyle?: React.CSSProperties;
  /** The type of the input element, such as text, email, tel, url, number, or password. */
  type?: 'text' | 'email' | 'tel' | 'url' | 'number' | 'password';
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const {
    id,
    label,
    error,
    prefix,
    suffix,
    onChange,
    helpText,
    disabled,
    inputStyle,
    rightSection,
    placeholder,
    defaultValue,
    type = 'text',
    labelClassName,
    inputClassName,
    ...others
  } = props;

  const uid = useUid(id);
  let rightSectionComponent = null;
  const rightSectionRef = React.useRef();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.currentTarget.value);
  };

  if (rightSection) {
    rightSectionComponent = React.cloneElement(rightSection as React.ReactElement, {
      className: classNames((rightSection as React.ReactElement)?.props.className, 'merge-left'),
      ref: rightSectionRef,
    });
  }

  return (
    <label htmlFor={uid} className={classNames({ error }, labelClassName)}>
      <p>{label}</p>
      <div className={classNames({ 'merged-inputs': rightSection, 'prefixed-input-container': prefix || suffix })}>
        {prefix && <span className="input-prefix">{prefix}</span>}
        <input
          id={uid}
          ref={ref}
          type={type}
          style={inputStyle}
          disabled={disabled}
          onChange={handleChange}
          placeholder={placeholder}
          defaultValue={defaultValue}
          className={classNames({ 'merge-right': rightSection, prefixed: prefix, suffixed: suffix }, inputClassName)}
          {...others}
        />
        {suffix && (
          <span
            className="input-suffix"
            style={{
              ...(rightSection
                ? {
                    paddingRight:
                      rightSectionRef?.current &&
                      (rightSectionRef.current as HTMLElement)?.getBoundingClientRect().width,
                  }
                : {}),
            }}
          >
            {suffix}
          </span>
        )}
        {rightSectionComponent}
      </div>
      <small>{error || helpText}</small>
    </label>
  );
});

TextField.displayName = 'TextField';
